import React, { useEffect, useState } from 'react';
import { UnmountClosed } from 'react-collapse';
import styled from 'styled-components';
import {
  NextIcon,
  TransportIcon,
  BodyLink,
  BodyText
} from '@kvdbil/components';
import ProcessObject from '~/App/shared/interfaces/ProcessObject';
import { useTranslation } from '~/Locale';
import Header from './Header';
import { CMSDataObjectPageService } from '~/App/views/ObjectPage/cmsDataHandlers';
import { ServiceStructuredText } from './ServiceStructuredText';
import { ServiceContainer } from '..';
import { useTransportCalculator } from '../../../../../../../shared/components/TransportCalculator/useTransportCalculator';
import Link from '~/App/shared/components/Link';
import { isRecreationalVehicle } from '~/helpers/auction';
import { formatPhoneNumberForLink } from '~/helpers/phoneNumber';
import DeliveryCalculator from '~/App/shared/components/DeliveryCalculator/DeliveryCalculator';
import { useCmsData } from '~/App/shared/hooks/useCmsData';
import {
  ObjectServicesDocument,
  ObjectServicesQuery
} from '~/config/generated/graphql';
import { ObjectServicesData } from '~/App/shared/interfaces/store/CmsData';

interface Props {
  processObject: ProcessObject;
}

const ServiceInformation = styled.div`
  padding: 1em 1em 2em 1em;
`;

const StyledLink = styled(Link)`
  display: inline-flex;
  align-items: center;
`;

const ServiceInformationUrlContainer = styled.div`
  text-align: center;
  margin-top: 0.5em;
`;

interface Props {
  processObject: ProcessObject;
  cmsData?: CMSDataObjectPageService;
}

export const TransportService = ({ processObject, cmsData }: Props) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { t } = useTranslation();
  const vehicleLocation = processObject?.locationInfo;
  const vehicleType = processObject?.vehicleType;
  const toggleExpanded = () => setIsExpanded(!isExpanded);
  const isRecreational = isRecreationalVehicle(processObject.objectType);
  const customerContact = t('CUSTOMER_CONTACT_PHONE');

  const {
    data: { deliveryCalculator: deliveryCalculatorCmsData }
  } = useCmsData<ObjectServicesQuery, ObjectServicesData>({
    storeKey: 'objectServices',
    query: ObjectServicesDocument
  });

  const { getFacilities: getTransportData } = useTransportCalculator();

  useEffect(() => {
    getTransportData([vehicleType]);
  }, [getTransportData, vehicleType]);

  if (!vehicleLocation) {
    return null;
  }

  const ariaControls = 'additional-service-transport-content';

  return (
    <ServiceContainer>
      <Header
        isExpanded={isExpanded}
        toggleExpanded={toggleExpanded}
        title={cmsData?.title ?? ''}
        icon={cmsData?.icon ?? TransportIcon}
        responsiveImage={cmsData?.image?.responsiveImage}
        description={
          !isRecreational
            ? (cmsData?.description ?? '')
            : t('Contact customer support')
        }
        ariaControls={ariaControls}
      />

      {cmsData && (
        <UnmountClosed isOpened={isExpanded}>
          <ServiceInformation id={ariaControls}>
            {isRecreational && (
              <>
                <BodyText>
                  {t('To get a transportation quote, please contact us at:')}
                </BodyText>
                <BodyText>
                  <a href={`tel:${formatPhoneNumberForLink(customerContact)}`}>
                    {customerContact}
                  </a>
                </BodyText>
              </>
            )}

            {!isRecreational && (
              <>
                <ServiceStructuredText data={cmsData?.content} />
                <DeliveryCalculator
                  facilityInfo={vehicleLocation?.facility}
                  cmsData={deliveryCalculatorCmsData}
                  processObjectId={processObject.id}
                />

                <ServiceInformationUrlContainer>
                  <StyledLink
                    to={cmsData?.link?.[0]?.url ?? ''}
                    target="_blank"
                  >
                    <BodyLink as="span">{cmsData?.link?.[0]?.text}</BodyLink>
                    <NextIcon />
                  </StyledLink>
                </ServiceInformationUrlContainer>
              </>
            )}
          </ServiceInformation>
        </UnmountClosed>
      )}
    </ServiceContainer>
  );
};

export default TransportService;
